import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useNewSkin from '../../hooks/useNewSkin';

import { focusNewMessageTextarea } from '../../helpers/focusManager';
import { trans } from '../../helpers/translations';

import {
    showUserDataModal as showUserDataModalAction,
    widgetActivityTracking,
} from '../../store/actions';
import { trackingEvents } from '../../store/activityTrackingHelpers';
import { DefaultRootState } from '../../store/typings';
import BottomSheet from '../BottomSheet/BottomSheet';
import { ChevronDown, ChevronDownLight } from '../svgIcons/SvgIcons';
import AlwaysOnlineModal from './AlwaysOnlineModal';
import CreateTicketModal from './CreateTicketModal';
import PrechatModal from './PrechatModal';
import { userDataModalClose, userDataModalStyle } from './UserDataModal.styles';

const UserDataModal = (): ReactElement => {
    const dispatch = useDispatch();
    const showUserDataModal = useSelector((store: DefaultRootState) => store.showUserDataModal);
    const { isNewSkin } = useNewSkin();
    const modalTypeRef = useRef(showUserDataModal);
    if (showUserDataModal) {
        // Keep last modal type in ref so the panel retains its content during the BottomSheet leave animation
        modalTypeRef.current = showUserDataModal;
    }

    const closeModal = useCallback((): void => {
        dispatch(widgetActivityTracking(trackingEvents.closeModalClicked));
        dispatch(showUserDataModalAction(false));
    }, [dispatch]);

    useEffect(() => {
        // focus newMessageTextarea after closing modal
        if (modalTypeRef.current && !showUserDataModal) {
            focusNewMessageTextarea();
        }
    }, [showUserDataModal]);

    return (
        <BottomSheet
            isOpen={showUserDataModal !== false}
            onClose={closeModal}
            shouldCloseOnBackdropClick={false}
        >
            <div data-testid="userDataModal" css={userDataModalStyle}>
                <button
                    css={userDataModalClose}
                    onClick={closeModal}
                    type="button"
                    aria-label={trans('closeUserDataModal', null, 'Close modal')}
                >
                    {isNewSkin ? <ChevronDownLight /> : <ChevronDown />}
                </button>

                {modalTypeRef.current === 'prechat' && <PrechatModal />}
                {modalTypeRef.current === 'alwaysOnline' && <AlwaysOnlineModal />}
                {modalTypeRef.current === 'createTicket' && <CreateTicketModal />}
            </div>
        </BottomSheet>
    );
};

export default UserDataModal;
